import { Controller, Form, useForm } from "react-hook-form";
import { usePaymentInputs } from "react-payment-inputs";
// @ts-expect-error It does in fact export this
import images from "react-payment-inputs/lib/images";
import { LabeledInput } from "@core/components/forms/LabeledInput";
import { Button } from "@core/components/ui/button";
import React, { useState } from "react";
import { LabeledSelect } from "@core/components/forms/LabeledSelect";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@core/components/ui/alert";
import { usePaymentContacts } from "@phoenix/payments/contexts/PaymentContactsContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@core/components/ui/dialog";
import { AddressForm } from "@core/components/forms/Address/AddressForm";
import { useAddressBook } from "@core/hooks/useAddressBook";
import { useApi } from "@core/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ContactModel } from "@/apidtos";
import ExtensionPoint from "@core/components/PipelineComponents/ExtensionPoint";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare } from "@fortawesome/pro-light-svg-icons";

export interface CreditCardFormValues {
  cardHolderName: string;
  cardNumber: string;
  cardExpiration: string;
  cvv: string;
  postalCode: string;
  name?: string;
  billingContactId?: number;
}

interface CreditCardFormProps {
  includeName?: boolean;
  includeContactSelect?: boolean;
  onSubmit: (data: CreditCardFormValues) => void;
}

export const CreditCardForm = (props: CreditCardFormProps) => {
  const { t } = useTranslation();
  const { contacts: contextContacts } = usePaymentContacts();
  const { data: addressBookData, reload } = useAddressBook();
  const api = useApi();
  const queryClient = useQueryClient();
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<ContactModel | null>(null);
  const {
    getCardNumberProps,
    getCVCProps,
    getCardImageProps,
    getExpiryDateProps,
    meta,
  } = usePaymentInputs();

  const {
    register,
    handleSubmit,
    control,
    setError,
    clearErrors,
    setValue,
    watch,
    formState,
  } = useForm<CreditCardFormValues>({
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUnregister: false,
    defaultValues: {
      cardHolderName: "",
      cardNumber: "",
      cardExpiration: "",
      cvv: "",
      postalCode: "",
      name: "",
      billingContactId: undefined,
    },
  });

  const billingContactId = watch("billingContactId");

  // Use address book data if available, otherwise fall back to context contacts
  // This ensures we have fresh data after creating an address
  const contacts = addressBookData?.map((c: any) => c.Secondary!).filter(Boolean) ?? contextContacts ?? [];
  const contactsExist = Boolean(contacts?.length);

  const selectedContact: ContactModel | undefined = contacts.find((c: ContactModel) => c.Id === billingContactId);
  const isAddressIncomplete = selectedContact != null && (!selectedContact.CountryId || !selectedContact.RegionId);

  // Set default billing contact when contacts become available
  React.useEffect(() => {
    if (props.includeContactSelect && contactsExist && !billingContactId && contacts[0]?.Id) {
      setValue("billingContactId", contacts[0].Id);
    }
  }, [contactsExist, contacts, billingContactId, props.includeContactSelect, setValue]);

  const updateAddressMutation = useMutation({
    mutationFn: async (contact: ContactModel) => {
      const newCustomerContactId = await api["/addressbook/update"]({ ...contact, IsActive: true, IsDeleted: false });
      reload();
      queryClient.invalidateQueries({ queryKey: ["addressBook"] });
      const updatedAddressBook = await queryClient.fetchQuery({
        queryKey: ["addressBook"],
        queryFn: () => api["/addressbook"](),
      });
      const newEntry = updatedAddressBook?.find((x: any) => x.Id === newCustomerContactId);
      return newEntry?.SecondaryId ?? newEntry?.Secondary?.Id ?? 0;
    },
  });

  const createAddressMutation = useMutation({
    mutationFn: async (contact: ContactModel) => {
      // Set IsPrimaryBilling to true
      const contactWithBilling = {
        ...contact,
        IsPrimaryBilling: true,
      };
      const newCustomerContactId = await api["/addressbook/add"](contactWithBilling);
      // Reload address book
      reload();
      queryClient.invalidateQueries({ queryKey: ["addressBook"] });
      // Refetch to get the new contact
      const updatedAddressBook = await queryClient.fetchQuery({
        queryKey: ["addressBook"],
        queryFn: () => api["/addressbook"](),
      });
      // Find the newly created contact
      let newlyAddedContact;
      if (newCustomerContactId === 0) {
        // Guest mode - find by matching the contact we just added (should be last in list)
        newlyAddedContact = updatedAddressBook?.find((x: any) =>
          x.Secondary?.Street1 === contact.Street1 &&
          x.Secondary?.City === contact.City
        );
      } else {
        // Logged in - find by CustomerContact Id
        newlyAddedContact = updatedAddressBook?.find((x: any) => x.Id === newCustomerContactId);
      }
      return newlyAddedContact?.SecondaryId ?? newlyAddedContact?.Secondary?.Id ?? 0;
    },
  });

  const handleAddressSubmit = async (contact: ContactModel) => {
    try {
      if (editingContact) {
        const newContactId = await updateAddressMutation.mutateAsync({ ...contact, Id: editingContact.Id });
        setAddressModalOpen(false);
        setEditingContact(null);
        await queryClient.refetchQueries({ queryKey: ["addressBook"] });
        if (newContactId) {
          setValue("billingContactId", newContactId, { shouldValidate: true });
        }
      } else {
        const newContactId = await createAddressMutation.mutateAsync(contact);
        setAddressModalOpen(false);
        await queryClient.refetchQueries({ queryKey: ["addressBook"] });
        if (newContactId) {
          setValue("billingContactId", newContactId, { shouldValidate: true });
        }
      }
    } catch (err) {
      console.error("Failed to save address:", err);
    }
  };

  React.useEffect(() => {
    if (meta.touchedInputs.cardNumber && meta.erroredInputs.cardNumber) {
      setError("cardNumber", { message: meta.erroredInputs.cardNumber })
    } else {
      clearErrors("cardNumber");
    }
    if (meta.touchedInputs.cvc && meta.erroredInputs.cvc) {
      setError("cvv", { message: meta.erroredInputs.cvc })
    } else {
      clearErrors("cvv");
    }
    if (meta.touchedInputs.expiryDate && meta.erroredInputs.expiryDate) {
      setError("cardExpiration", { message: meta.erroredInputs.expiryDate })
    } else {
      clearErrors("cardExpiration");
    }
  }, [ meta.erroredInputs, meta.touchedInputs, setError, clearErrors ]);

  const submit = (data: CreditCardFormValues) => {
    const errors = Object.values(meta.erroredInputs).filter(Boolean);
    if (errors.length) {
      return;
    }
    props.onSubmit(data);
  };

  return (
    <>
      <Form<CreditCardFormValues> control={control} onSubmit={({ data }) => submit(data)}>
        <div className="grid grid-cols-2 gap-2 pb-2">
          {props.includeContactSelect && (
            <div className="col-span-2">
              <Controller
                control={control}
                name="billingContactId"
                rules={{ required: props.includeContactSelect ? t("ui.billingContactRequired", "Billing contact is required") : false }}
                render={({ field }) => (
                  <LabeledSelect
                    className="col-span-2"
                    label={t("ui.billingContact", "Billing Contact")}
                    value={field.value?.toString() || (contactsExist ? contacts[0]?.Id?.toString() || "" : "")}
                    onChange={(e) => {
                      if (e.target.value === "create") {
                        setEditingContact(null);
                        setAddressModalOpen(true);
                      } else if (e.target.value) {
                        field.onChange(parseInt(e.target.value));
                      } else {
                        field.onChange(undefined);
                      }
                    }}>
                    {!contactsExist && <option value="">{t("ui.selectBillingContact", "Select Billing Contact")}</option>}
                    {contacts.map(c => (
                      <option key={c.Id} value={c.Id}>
                        {c.Street1} {c.City ? `- ${c.City}` : ""}
                      </option>
                    ))}
                    <option value="create">{t("ui.createAddress", "Create Address")}</option>
                  </LabeledSelect>
                )}
              />
            </div>
          )}
          {props.includeContactSelect && isAddressIncomplete && (
            <div className="col-span-2">
              <Alert className="py-2 border-amber-500/60 bg-amber-500/15 text-amber-200">
                <AlertDescription className="flex items-center justify-between gap-2 text-sm">
                  <span>{t("ui.billingAddressMissingFields", "Billing address is missing State and/or Country.")}</span>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 underline whitespace-nowrap"
                    onClick={() => { setEditingContact(selectedContact!); setAddressModalOpen(true); }}
                  >
                    <FontAwesomeIcon icon={faPenToSquare} />
                    {t("ui.editAddress", "Edit Address")}
                  </button>
                </AlertDescription>
              </Alert>
            </div>
          )}
        {props.includeName && <LabeledInput
          className="col-span-2"
          {...register("name", { required: true })}
          label={t("ui.nickname", "Nickname")} />}
        <LabeledInput
          className="col-span-2"
          {...register("cardHolderName", { required: true })}
          label={t("ui.cardHolderName", "Card Holder Name")} />
        <Controller
          control={control}
          name="cardNumber"
          rules={{ required: true }}
          render={({ field }) => (
            <div className="col-span-2">
              <LabeledInput
                {...field}
                {...getCardNumberProps({ onChange: field.onChange })}
                label={t("ui.cardNumber", "Card Number")}
                placeholder={t("ui.cardNumber", "Card Number")}>
                <div className="absolute right-2 h-full flex items-center">
                  <svg {...getCardImageProps({ images })} fontSize="24"></svg>
                </div>
              </LabeledInput>
              {formState.errors.cardNumber && <p className="text-danger-500">{formState.errors.cardNumber.message}</p>}
            </div>
          )}
        />
        <Controller
          control={control}
          name="cardExpiration"
          rules={{ required: true }}
          render={({ field }) => (
            <div>
              <LabeledInput
                {...field}
                {...getExpiryDateProps({ onChange: field.onChange })}
                label={t("ui.expirationDate", "Expiration Date")} />
              {formState.errors.cardExpiration && <p className="text-danger-500">{formState.errors.cardExpiration.message}</p>}
            </div>
          )}
        />
        <Controller
          control={control}
          name="cvv"
          rules={{ required: true }}
          render={({ field }) => (
            <div>
              <LabeledInput
                {...field}
                {...getCVCProps({ onChange: field.onChange })}
                label={t("ui.cvv", "CVV")}
                placeholder={t("ui.cvv", "CVV")} />
              {formState.errors.cvv && <p className="text-danger-500">{formState.errors.cvv.message}</p>}
            </div>
          )}
        />
      </div>
        <Button type="submit" disabled={!formState.isValid || (props.includeContactSelect && !billingContactId) || isAddressIncomplete}>
          {t("ui.submit", "Submit")}
        </Button>
      </Form>
      <ExtensionPoint name="_site.dashboard.wallet.create.footer" />
      {props.includeContactSelect && (
        <Dialog open={addressModalOpen} onOpenChange={(open) => { setAddressModalOpen(open); if (!open) setEditingContact(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingContact ? t("ui.editAddress", "Edit Address") : t("ui.createAddress", "Create Address")}
              </DialogTitle>
            </DialogHeader>
            <DialogDescription className="sr-only">
              {editingContact ? t("ui.editBillingAddress", "Edit billing address") : t("ui.createBillingAddress", "Create a new billing address")}
            </DialogDescription>
            <AddressForm
              id="addressModalForm"
              contact={editingContact ?? undefined}
              onSubmit={handleAddressSubmit}
            />
            <DialogFooter>
              <Button type="submit" form="addressModalForm">
                {t("ui.confirm", "Confirm")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
